import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Upload, FileText, Check, X, Plus, Trash2, Calendar, Download, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface DraftRow {
  id: string;
  content: string;
  platform: string;
  scheduled_for: string;
  account_id: string;
  status: "pending" | "error" | "done";
  error?: string;
}

const CSV_TEMPLATE = `content,platform,scheduled_for\n"Your post content here","instagram","2026-07-15 10:00"\n"Another post for Twitter","twitter","2026-07-16 14:30"`;

export function BulkSchedulerView() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [rows, setRows]       = useState<DraftRow[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [tab, setTab]         = useState<"upload" | "manual">("upload");

  const { data: accounts = [] } = useQuery({
    queryKey: ["bulk-scheduler-accounts", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = supabase.from("social_accounts").select("id,platform,account_handle").eq("user_id", user.id).eq("is_connected", true);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return (data ?? []) as any[];
    },
  });

  const { data: recentJobs = [] } = useQuery({
    queryKey: ["bulk-upload-jobs", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data } = await (supabase as any).from("bulk_upload_jobs").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(5);
      return (data ?? []) as any[];
    },
  });

  const parseCSV = (text: string): DraftRow[] => {
    const lines = text.trim().split("\n").filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map(h => h.replace(/"/g, "").trim());
    return lines.slice(1).map((line, i) => {
      // naive CSV parse (handles quoted fields)
      const vals: string[] = [];
      let cur = "", inQ = false;
      for (const ch of line) {
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === "," && !inQ) { vals.push(cur.trim()); cur = ""; continue; }
        cur += ch;
      }
      vals.push(cur.trim());

      const row: Record<string, string> = {};
      headers.forEach((h, j) => { row[h] = vals[j] ?? ""; });

      const platform = row["platform"]?.toLowerCase() ?? "instagram";
      const matchedAcc = accounts.find((a: any) => a.platform === platform);

      return {
        id: `r-${i}-${Date.now()}`,
        content: row["content"] ?? "",
        platform,
        scheduled_for: row["scheduled_for"] ?? "",
        account_id: matchedAcc?.id ?? "",
        status: "pending",
      };
    });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCSV(ev.target?.result as string);
      setRows(parsed);
      if (parsed.length === 0) toast({ title: "No rows found", description: "Check your CSV format.", variant: "destructive" });
    };
    reader.readAsText(file);
  };

  const addManualRow = () => {
    setRows(r => [...r, {
      id: `m-${Date.now()}`,
      content: "", platform: "instagram",
      scheduled_for: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
      account_id: accounts.find((a: any) => a.platform === "instagram")?.id ?? "",
      status: "pending",
    }]);
  };

  const updateRow = (id: string, field: keyof DraftRow, val: string) => {
    setRows(r => r.map(row => {
      if (row.id !== id) return row;
      const updated = { ...row, [field]: val };
      if (field === "platform") {
        updated.account_id = accounts.find((a: any) => a.platform === val)?.id ?? "";
      }
      return updated;
    }));
  };

  const removeRow = (id: string) => setRows(r => r.filter(row => row.id !== id));

  const handlePublish = async () => {
    if (rows.length === 0) return;
    setPublishing(true);

    const { data: { user } } = await supabase.auth.getUser();
    // Create bulk job record
    const { data: job } = await (supabase as any).from("bulk_upload_jobs").insert({
      org_id: orgId,
      user_id: user?.id,
      status: "processing",
      total_rows: rows.length,
    }).select().single();

    let processed = 0, failed = 0;
    const errors: any[] = [];

    for (const row of rows) {
      try {
        if (!row.content.trim()) throw new Error("Content is required");
        if (!row.account_id) throw new Error(`No connected ${row.platform} account`);

        const scheduledDate = row.scheduled_for ? new Date(row.scheduled_for) : null;
        const status = scheduledDate && scheduledDate > new Date() ? "scheduled" : "draft";

        const { error } = await (supabase as any).from("posts").insert({
          user_id: user?.id,
          org_id: orgId,
          social_account_id: row.account_id,
          content: row.content,
          platform: row.platform,
          platforms: [row.platform],
          social_account_ids: [row.account_id],
          status,
          scheduled_for: scheduledDate?.toISOString() ?? null,
        });
        if (error) throw error;

        setRows(r => r.map(x => x.id === row.id ? { ...x, status: "done" } : x));
        processed++;
      } catch (e: any) {
        setRows(r => r.map(x => x.id === row.id ? { ...x, status: "error", error: e.message } : x));
        errors.push({ row: row.id, error: e.message });
        failed++;
      }
    }

    if (job) {
      await (supabase as any).from("bulk_upload_jobs").update({
        status: "complete",
        processed_rows: processed,
        failed_rows: failed,
        errors,
      }).eq("id", job.id);
    }

    setPublishing(false);
    toast({
      title: `Bulk schedule complete`,
      description: `${processed} posts scheduled, ${failed} failed.`,
      variant: failed > 0 ? "destructive" : "default",
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bulk-posts-template.csv";
    a.click();
  };

  const pending = rows.filter(r => r.status === "pending").length;
  const done    = rows.filter(r => r.status === "done").length;
  const errors2 = rows.filter(r => r.status === "error").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" /> Bulk Scheduler
          </h2>
          <p className="text-sm text-muted-foreground">Schedule multiple posts at once via CSV or manual entry</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> CSV Template
          </Button>
          <Button size="sm" onClick={addManualRow}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Row
          </Button>
        </div>
      </div>

      {/* Upload zone */}
      <Card className="p-6">
        <div className="flex gap-2 mb-4">
          <Button size="sm" variant={tab === "upload" ? "default" : "outline"} onClick={() => setTab("upload")}>
            <Upload className="w-3.5 h-3.5 mr-1.5" /> CSV Upload
          </Button>
          <Button size="sm" variant={tab === "manual" ? "default" : "outline"} onClick={() => setTab("manual")}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Manual Entry
          </Button>
        </div>

        {tab === "upload" && (
          <div
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all"
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <p className="font-medium text-sm">Drop a CSV file here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">Columns: content, platform, scheduled_for</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
          </div>
        )}
      </Card>

      {/* Draft rows table */}
      {rows.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-sm">{rows.length} Posts</h3>
              {done > 0    && <Badge className="bg-green-100 text-green-700 text-[10px]">{done} scheduled</Badge>}
              {errors2 > 0 && <Badge variant="destructive" className="text-[10px]">{errors2} failed</Badge>}
            </div>
            <Button size="sm" onClick={handlePublish} disabled={publishing || pending === 0}>
              {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5 mr-1.5" />}
              {publishing ? "Scheduling…" : `Schedule ${pending}`}
            </Button>
          </div>

          <div className="space-y-3">
            {rows.map(row => (
              <div key={row.id} className={`p-3 rounded-lg border transition-colors ${
                row.status === "done"  ? "border-green-200 bg-green-50/50" :
                row.status === "error" ? "border-red-200 bg-red-50/50" :
                "border-border"
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
                  <div className="md:col-span-2">
                    <Textarea
                      value={row.content}
                      onChange={e => updateRow(row.id, "content", e.target.value)}
                      placeholder="Post content…"
                      rows={2}
                      disabled={row.status !== "pending"}
                      className="resize-none text-sm"
                    />
                  </div>
                  <Select value={row.platform} onValueChange={v => updateRow(row.id, "platform", v)} disabled={row.status !== "pending"}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {accounts.map((a: any) => (
                        <SelectItem key={a.id} value={a.platform} className="capitalize">
                          {a.platform} · @{a.account_handle}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 items-start">
                    <Input
                      type="datetime-local"
                      value={row.scheduled_for}
                      onChange={e => updateRow(row.id, "scheduled_for", e.target.value)}
                      disabled={row.status !== "pending"}
                      className="h-8 text-sm flex-1"
                    />
                    {row.status === "pending" && (
                      <Button size="icon" variant="ghost" className="w-7 h-7 shrink-0 text-destructive" onClick={() => removeRow(row.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {row.status === "done"  && <Check className="w-4 h-4 text-green-600 mt-1.5 shrink-0" />}
                    {row.status === "error" && <X className="w-4 h-4 text-red-500 mt-1.5 shrink-0" aria-label={row.error} />}
                  </div>
                </div>
                {row.error && <p className="text-xs text-red-500 mt-2">{row.error}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent jobs */}
      {recentJobs.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" /> Recent Upload Jobs
          </h3>
          <div className="space-y-2">
            {recentJobs.map((job: any) => (
              <div key={job.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium capitalize">{job.status}</p>
                  <p className="text-xs text-muted-foreground">{job.total_rows} posts · {job.processed_rows} done · {job.failed_rows} failed</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={job.status === "complete" ? "default" : "secondary"} className="text-[10px] capitalize">{job.status}</Badge>
                  <span className="text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
